import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

export const AthleteCoachedWarmupRoutinePhases = () => {
  const { athlete } = useAthleteAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templateTitle, setTemplateTitle] = useState("");
  const [phases, setPhases] = useState([]);

  useEffect(() => {
    // Check if data is passed from exercises page
    if (location.state?.phases && location.state?.template_title) {
      setTemplateTitle(location.state.template_title);
      setPhases(location.state.phases);
      setIsLoading(false);
      return; // ❌ skip API call
    }

    // Otherwise fetch from API
    const fetchWarmupRoutine = async () => {
      try {
        const url = `${API_URL.FRONTEND_ATHLETE_COACHED_WARMUPROUTINE_PHASES}${athlete.id}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${athlete.token}`,
          },
        });

        const json = await response.json();

        if (!response.ok) {
          setError(json.message || "Failed to load routine");
          return;
        }

        setTemplateTitle(json.template_title || "Warmup Routine");
        setPhases(json.phases || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarmupRoutine();
  }, [athlete, location.state]);

  const handlePhaseClick = (phase) => {
    if (phase.type === "RestPhase") return;

    navigate(`/athlete/coached/warmup-routine-phase-exercises`, {
      state: {
        phase,
        template_title: templateTitle,
        phases, // pass all phases so we can come back without API call
      },
    });
  };

  const handleAmaxSpeedWorkoutClick = () => {
    navigate(`/athlete/coached/speed-workout`, {
      state: {
        phases, // pass all phases so we can come back without API call
      },
    });
  };

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div>
            <AthleteHeader />
          </div>

          {/* TEMPLATE TITLE */}
          <div className="w-full max-w-2xl mx-auto px-5">
            <div className="pb-3 flex flex-row justify-center items-center gap-10 bg-white border-b border-black/30">
              <p className="text-black text-xl">{templateTitle}</p>
            </div>
          </div>

          <div className="w-full max-w-2xl mx-auto">
            <div className="text-white py-3">
              {/* TABS */}
              <div className="px-5 mb-4">
                <div className="bg-[#f5f4f4] rounded-2xl flex justify-between gap-8 px-4 py-4">
                  <button className="text-black text-sm xs:text-base">
                    Warmup
                  </button>
                  <button
                    onClick={handleAmaxSpeedWorkoutClick}
                    className="text-lightSmock text-sm xs:text-base"
                  >
                    Workout
                  </button>
                </div>
              </div>

              {isLoading ? (
                <p className="text-black text-center">
                  Loading warmup routine…
                </p>
              ) : error ? (
                <div className="bg-red-100 text-red-500 p-3 rounded mb-4">
                  {error}
                </div>
              ) : (
                <div className="space-y-[15px] px-5 pb-5">
                  {phases.map((phase) => (
                    <button
                      key={phase.phase_id}
                      onClick={() => handlePhaseClick(phase)}
                      className={`w-full p-[14px] border border-black/90 bg-transparent rounded-xl hover:bg-grayish/10 transition text-start
                    ${phase.type === "RestPhase" ? "cursor-default" : "bg-transparent hover:bg-grayish/10"}`}
                    >
                      <p className="text-black text-base xs:text-lg font-medium">
                        {phase.title}
                      </p>

                      {phase.type === "RestPhase" && (
                        <div className="mt-1 text-sm text-grayish">
                          {phase.rest_duration && (
                            <p className="inline-block">
                              ⏱
                              <span className="ml-0.5 text-sm xs:text-base font-medium">
                                {phase.rest_duration}
                              </span>
                            </p>
                          )}
                          {phase.rest_notes && (
                            <p className="block text-sm xs:text-base font-medium italic">
                              {phase.rest_notes}
                            </p>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};
